import React, { useEffect, useState } from "react";
import { ContentWrapper, WrapperCard } from "@components/index";
import { useNavigate, useParams } from "react-router-dom";
import { Space, Typography, Row, Col } from "antd";
import { Icon } from "@iconify/react";
import { useNotification } from "@hooks/useNotification";
import { isEmpty } from "lodash";
import moment from "moment";
import { templateApi } from "../../../../../../../Services/template.api";
import styles from "@styles/variables.module.scss";

const getItemText = (name?: string, value?: any, isBoolean?: any) => {
  return (
    <Space direction="vertical" size={8} style={{ marginBottom: 24, paddingRight: "20px" }}>
      <Typography.Text type="secondary" style={{ fontSize: 13 }}>{name}</Typography.Text>
      <Typography.Text>
        {!isBoolean ? value || "-" : value === true || value === "true" ? "Enabled" : "Disabled"}
      </Typography.Text>
    </Space>
  );
};

export const TemplateDrilldown = (props: any) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const { openToast } = useNotification();
  const [data, setData] = useState<{ [key: string]: any }>({});

  useEffect(() => {
    if (id) {
      setIsLoading(true);
      templateApi
        .getById(id)
        .then((res: any) => {
          if (res) {
            setData(res);
          }
          setIsLoading(false);
        })
        .catch((err) => {
          setIsLoading(false);
          openToast({ content: err?.message || "Failed to fetch template details", type: "error" });
        });
    }
  }, [id]);

  if (isEmpty(data)) {
    return <ContentWrapper loading={isLoading}> </ContentWrapper>;
  }

  return (
    <>
      <ContentWrapper
        loading={isLoading}
        customTitle={
          <Space style={{ alignItems: "center", lineHeight: 1 }}>
            <Icon
              onClick={() => navigate(`/workorder-manage-catalog?activeTab=template`)}
              icon="akar-icons:arrow-left"
              style={{
                fontSize: "1.8rem",
                cursor: "pointer",
                color: styles?.primary || "#1890ff",
                marginRight: 8,
              }}
            />
            <Typography.Title level={3} style={{ margin: 0 }}>
              Template Details
            </Typography.Title>
          </Space>
        }
      >
        <Row>
          <Col span={24} style={{ marginBottom: 24 }}>
            <WrapperCard
              title="Template Details"
              smallHeader
              action={
                <Icon
                  icon="carbon:edit"
                  style={{
                    color: styles?.strawberry || "#ff4d4f",
                    cursor: "pointer",
                    fontSize: 20,
                    verticalAlign: "middle",
                  }}
                  onClick={() => navigate(`/workorder-manage-catalog/template-edit?id=${id}`)}
                />
              }
            >
              <Row gutter={16}>
                <Col xs={24} md={8}>
                  {getItemText("Template Name", data?.templateName)}
                </Col>
              </Row>

              {[
                { key: "reviewMail", label: "For Review" },
                { key: "copyMail", label: "For Feedback" },
                { key: "reSubmitMail", label: "Re-Submit Email" },
                { key: "higherAuthorityMail", label: "Document Version Email (For Reviewer & Approver)" },
                { key: "creatorMail", label: "Document Version Email (For Creator)" },
                { key: "approveMail", label: "Approved Email" },
                { key: "completeMail", label: "For Completed" },
                { key: "declineMail", label: "For Declined" },
                { key: "reminderMail", label: "Reminder email" },
                { key: "expiryMail", label: "Expiry Email" },
                // { key: "oneStopMail", label: "Void Email" }, // hidden for now
                { key: "cancelMail", label: "Cancel Email" },
              ].map((mailConfig) => (
                <React.Fragment key={mailConfig.key}>
                  <Row>
                    <Typography.Title level={5} style={{ marginTop: 24, marginBottom: 16 }}>
                      {mailConfig.label}
                    </Typography.Title>
                  </Row>
                  <Row>
                    <Col xs={24} md={8}>
                      {getItemText("Email Subject", data?.[mailConfig.key]?.emailSubject)}
                    </Col>
                    <Col xs={24} md={16}>
                      {getItemText("Email Message", data?.[mailConfig.key]?.emailMessage)}
                    </Col>
                  </Row>
                </React.Fragment>
              ))}

              <Row>
                <Col xs={24}>
                  <Typography.Title level={4} style={{ margin: "16px 0 4px 0" }}>
                    Custom Options
                  </Typography.Title>
                </Col>
                <Col xs={24}>
                  <Typography.Title level={5} style={{ margin: "8px 0" }}>
                    Reminders
                  </Typography.Title>
                </Col>
                <Col xs={24} md={8}>
                  {getItemText(
                    "Send Automatic Reminders",
                    data?.reminder?.sendAutomaticRemainders,
                    true
                  )}
                </Col>
                <Col xs={24} md={8}>
                  {getItemText(
                    "Days until first reminder",
                    data?.reminder?.sendAutomaticRemainders ? data?.reminder?.daysUntilFirstRemainder + " day(s)" : "-"
                  )}
                </Col>
                <Col xs={24} md={8}>
                  {getItemText(
                    "Days between reminder",
                    data?.reminder?.sendAutomaticRemainders ? data?.reminder?.daysBetweenRemainder + " day(s)" : "-"
                  )}
                </Col>
              </Row>

              <Row>
                <Col xs={24}>
                  <Typography.Title level={5} style={{ margin: "16px 0 4px 0" }}>
                    Expiration
                  </Typography.Title>
                </Col>
                <Col xs={24} md={8}>
                  {getItemText(
                    "Document will expire on",
                    data?.expiration?.expiryEnabled && data?.expiration?.expiryAfter
                      ? moment().add(Number(data.expiration.expiryAfter), "days").format("DD MMM YYYY")
                      : "-"
                  )}
                </Col>
                <Col xs={24} md={8}>
                  {getItemText(
                    "Days to warn approvers before expiration",
                    data?.expiration?.expiryEnabled && data?.expiration?.expiryWarnBefore
                      ? data.expiration.expiryWarnBefore + " day(s)"
                      : "-"
                  )}
                </Col>
              </Row>
            </WrapperCard>
          </Col>
        </Row>
      </ContentWrapper>
    </>
  );
};
